import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,  
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import { Form, Formik } from "formik";
import moment from "moment-timezone";
import { EditorState, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import htmlToDraft from "html-to-draftjs";
import { useOutletContext } from "react-router-dom";
import { TimezoneSelectForm } from "./TimezoneSelectForm";
import { LocationForm } from "./LocationForm";
import { DescriptionForm } from "./DescriptionForm";
import { DateTimeForm } from "./DateTimeForm";
import { NameForm } from "./NameForm";
import { BottomButtons } from "./BottomButtons";
import { FormPrompt } from "../../FormPrompt/FormPrompt";
import {
  updateEvent,
  createEvent,
} from "../../../redux/features/adminEventsSlice";
import SquareLocationPicker from "../../SquareLocationPicker";
import RoomPicker from "../../RoomPicker/RoomPicker";
import {
  fetchRooms,
  fetchShows,
  showsAndRoomsSliceSelector,
} from "../../../redux/features/showsAndRoomsSlice";
import ShowPicker from "../../ShowPicker/ShowPicker";
import logger from "../../../utils/logger";
import { PerformersForm } from "./PerformersForm";
import { createEventValidationSchema } from "./validation";
import { buildValuesPayload } from "./formUtils";
import { getUtcOffsetFromTimezoneName } from "../../../utils";
import {
  fetchPerformers,
  performersSliceSelector,
} from "../../../redux/features/performersSlice";
import {
  assignTrackingPixelsToEvent,
  createTrackingPixel,
  fetchTrackingPixelsByOrganization,
  trackingPixelsSliceSelector,
} from "../../../redux/features/trackingPixelsSlice";

const editorStateFromHtml = (html) => {
  const contentBlock = htmlToDraft(html);
  if (contentBlock?.contentBlocks?.length) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
    );
    return EditorState.createWithContent(contentState);
  }
  return EditorState.createEmpty();
};

const buildFormik = (event, orgPerformersMap = {}, trackingPixels = []) => {
  const {
    name,
    performers,
    description,
    address,
    timezone,
    startsAt,
    endsAt,
    squareLocation,
    showId,
    roomId,
    sortPriority,
    showInIndex,
    showInShowList,
    trackingPixelId,
    trackingPixelIds,
  } = event;
  const {
    placeName,
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    addressTimezone,
  } = address || {};

  // Use the extracted validation schema
  const validationSchema = createEventValidationSchema();

  const startsAtDate = startsAt ? moment.tz(startsAt.toDate(), timezone) : null;
  const startsAtTime = startsAt ? moment.tz(startsAt.toDate(), timezone) : null;
  const endsAtDate = endsAt ? moment.tz(endsAt.toDate(), timezone) : null;
  const endsAtTime = endsAt ? moment.tz(endsAt.toDate(), timezone) : null;

  const sanitizedPerformers = (() => {
    const list = performers || [];
    if (list.length === 0) return [];
    const first = list[0];
    if (first.performerId) {
      return list.map((p, index) => ({
        performerId: p.performerId,
        name: p.name ?? "",
        email: p.email ?? "",
        links: Array.isArray(p.links) ? p.links : [],
        bio: p.bio ?? "",
        credits: p.credits ?? "",
        headshotUrl: p.headshotUrl ?? "",
        order: p.order ?? index,
        isHeadliner: p.isHeadliner ?? false,
      }));
    }
    const nameKey = (p) => (p.name || "").trim().toLowerCase();
    const orgList = Object.values(orgPerformersMap);
    return list
      .map((oldP, index) => {
        const nameStr = (oldP.name || "").trim().toLowerCase();
        const match = orgList.find((o) => nameKey(o) === nameStr);
        if (!match) return null;
        return {
          performerId: match.id,
          name: match.name ?? "",
          email: match.email ?? "",
          links: Array.isArray(match.links) ? match.links : [],
          bio: match.bio ?? "",
          credits: match.credits ?? "",
          headshotUrl: match.headshotUrl ?? "",
          order: index,
          isHeadliner: false,
        };
      })
      .filter(Boolean);
  })();

  const selectedTrackingPixels = trackingPixels
    .filter((pixel) => (pixel.useForEvents || []).includes(event.id))
    .map((pixel) => pixel.id);

  const initialValues = {
    name,
    performers: sanitizedPerformers,
    description,
    placeName,
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    addressTimezone,
    startsAtDate,
    startsAtTime,
    endsAtDate,
    endsAtTime,
    timezone,
    squareLocation,
    showId,
    roomId,
    sortPriority: sortPriority ?? 0,
    showInIndex: showInIndex !== undefined ? showInIndex : false,
    showInShowList: showInShowList !== undefined ? showInShowList : false,
    trackingPixelIds:
      (Array.isArray(trackingPixelIds) && trackingPixelIds) ||
      (trackingPixelId ? [trackingPixelId] : selectedTrackingPixels),
  };

  return { validationSchema, initialValues };
};

const EventBasicInfo = () => {
  const { event, creatingNew, organization } = useOutletContext();
  const showsAndRoomsState = useSelector((state) =>
    showsAndRoomsSliceSelector(state),
  );
  const performersState = useSelector((state) => performersSliceSelector(state));
  const trackingPixelsState = useSelector((state) =>
    trackingPixelsSliceSelector(state),
  );
  const {
    id: organizationId,
    addressHistory,
    squareLocations,
  } = organization;
  const {
    rooms,
    shows,
    showsFetched,
    roomsFetched,
    showsLoading,
    roomsLoading,
  } = showsAndRoomsState;
  const { performers: orgPerformersMap = {}, performersOrganizationId } = performersState;
  const { byOrganization: trackingPixelsByOrganization = {} } =
    trackingPixelsState || {};
  const { pixels: trackingPixels = [] } =
    trackingPixelsByOrganization[organizationId] || {};
  const organizationPerformers = organizationId && performersOrganizationId === organizationId
    ? Object.values(orgPerformersMap)
    : [];
  const { id: eventId, loading } = event;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (organization && !roomsLoading && !roomsFetched) {
      dispatch(fetchRooms({ organizationId }));
    }
  }, [dispatch, roomsLoading, roomsFetched, organization, organizationId]);

  useEffect(() => {
    if (organization && !showsLoading && !showsFetched) {
      dispatch(fetchShows({ organizationId }));
    }
  }, [dispatch, showsLoading, showsFetched, organization, organizationId]);

  useEffect(() => {
    if (organizationId && performersOrganizationId !== organizationId) {
      dispatch(fetchPerformers({ organizationId }));
    }
  }, [dispatch, organizationId, performersOrganizationId]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
    }
  }, [dispatch, organizationId]);

  const handleSaveChanges = async (values) => {
    await dispatch(updateEvent({ eventId, values }));
  };
  const handleCreateNew = async (values) => {
    await dispatch(createEvent({ values }));
  };
  const handleCancelNew = () => {
    navigate("/events");
  };

  const { options: timezoneOptions } = useTimezoneSelect({
    labelStyle: "original",
    allTimezones,
  });

  const descriptionHtml = event?.description || "";
  const [descriptionState, setDescriptionState] = React.useState(
    () => editorStateFromHtml(descriptionHtml),
  );
  const [addPixelOpen, setAddPixelOpen] = React.useState(false);
  const [newPixelValues, setNewPixelValues] = React.useState({
    name: "",
    type: "meta",
    pixelId: "",
  });

  // Sync WYSIWYG editor when event description loads (e.g. after refresh)
  useEffect(() => {
    setDescriptionState(editorStateFromHtml(descriptionHtml));
  }, [descriptionHtml]);

  if (loading) {
    return;
  }

  const { validationSchema, initialValues } = buildFormik(
    event,
    orgPerformersMap,
    trackingPixels,
  );

  const roomsList = Object.keys(rooms).map((roomId) => rooms[roomId]);
  const showsList = Object.keys(shows).map((showId) => shows[showId]);
  return (
    <Box
      sx={{
        paddingTop: "30px",
        paddingBottom: "90px",
        position: "relative",
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, submitProps) => {
          if (creatingNew) {
            handleCreateNew(buildValuesPayload(values));
          } else {
            await handleSaveChanges(buildValuesPayload(values));
            await dispatch(
              assignTrackingPixelsToEvent({
                organizationId,
                eventId,
                pixelIds: values.trackingPixelIds || [],
              }),
            );
            submitProps.resetForm({ values });
          }
        }}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          dirty,
          setValues,
          setFieldValue,
          handleReset,
          setFieldTouched,
        }) => (
          <Form noValidate autoComplete="off">
            <FormPrompt hasUnsavedChanges={dirty} />
            <NameForm
              formikProps={{
                values,
                setValues,
                handleChange,
                handleBlur,
                touched,
                errors,
              }}
            />

            <ShowPicker
              formikProps={{
                values,
                handleChange,
                setFieldValue,
                setValues,
                handleBlur,
                touched,
                errors,
              }}
              shows={showsList}
              handleChange={async (e) => {
                // Clear show-related fields when show is cleared
                if (!e.target.value) {
                  setFieldValue("showInShowList", false);
                  setFieldValue("sortPriority", 0);
                }
              }}
            />
            {values.showId && (
              <Box sx={{ marginTop: "10px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.showInShowList === true}
                        onChange={(e) => {
                          setFieldValue("showInShowList", e.target.checked);
                        }}
                        name="showInShowList"
                      />
                    }
                    label="Include in Show's event list"
                  />
                  <Tooltip title="When enabled, this event will appear on the show's public landing page. This setting only applies if the show is marked as public-facing.">
                    <IconButton size="small" sx={{ padding: 0.5 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {values.showInShowList && (
                  <Box sx={{ marginTop: "20px", display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      id="sortPriority"
                      name="sortPriority"
                      label="Sort Priority"
                      type="number"
                      inputProps={{ min: -5, max: 5 }}
                      value={values.sortPriority ?? 0}
                      onChange={(e) => {
                        const value =
                          e.target.value === ""
                            ? 0
                            : parseInt(e.target.value, 10);
                        if (!isNaN(value)) {
                          // Clamp value between -5 and 5
                          const clampedValue = Math.max(-5, Math.min(5, value));
                          setFieldValue("sortPriority", clampedValue);
                        } else {
                          setFieldValue("sortPriority", 0);
                        }
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.sortPriority && errors.sortPriority)}
                      helperText={
                        touched.sortPriority && errors.sortPriority
                          ? errors.sortPriority
                          : "Range: -5 to 5. Higher numbers appear first."
                      }
                      sx={{ width: "120px" }}
                    />
                    <Tooltip title="Controls the order of events on the show's event list. Higher numbers appear first. Events with the same priority are sorted by date.">
                      <IconButton size="small" sx={{ padding: 0.5 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}
            <RoomPicker
              formikProps={{
                values,
                handleChange,
                setFieldValue,
                setValues,
                handleBlur,
                touched,
                errors,
              }}
              rooms={roomsList}
              handleChange={(e) => {
                const roomId = e.target.value;
                // If room is cleared (empty string), don't try to populate fields
                if (!roomId) {
                  return;
                }
                const { name, squareLocation, address } = rooms[roomId];
                const {
                  addressLine1,
                  addressLine2,
                  city,
                  state,
                  zipcode,
                  addressTimezone,
                } = address;

                if (Boolean(squareLocation)) {
                  setFieldValue("squareLocation", squareLocation);
                }
                setFieldValue("placeName", name);
                setFieldValue("addressLine1", addressLine1);
                setFieldValue("addressLine2", addressLine2);
                setFieldValue("city", city);
                setFieldValue("state", state);
                setFieldValue("zipcode", zipcode);
                setFieldValue("addressTimezone", addressTimezone);
                if (Boolean(addressTimezone)) {
                  let correctTimezone;
                  if (
                    timezoneOptions
                      .map(({ value }) => value)
                      .includes(addressTimezone)
                  ) {
                    correctTimezone = addressTimezone;
                  } else {
                    try {
                      const utcOffset =
                        getUtcOffsetFromTimezoneName(addressTimezone);
                      const timezone = timezoneOptions.find(
                        ({ offset }) => utcOffset / 60 === offset,
                      );
                      if (Boolean(timezone)) {
                        correctTimezone = timezone.value;
                      }
                    } catch (e) {
                      logger.info("invalid timezone", addressTimezone);
                    }
                  }
                  if (correctTimezone) {
                    setFieldValue("timezone", correctTimezone);
                  }
                }
              }}
            />

            <SquareLocationPicker
              required
              formikProps={{
                values,
                handleChange,
                setFieldValue,
                setValues,
                handleBlur,
                touched,
                errors,
              }}
              squareLocations={squareLocations}
            />

            <Box sx={{ marginTop: "20px", marginBottom: "20px" }}>
              <TextField
                select
                fullWidth
                id="trackingPixelId"
                name="trackingPixelIds"
                label="Meta Tracking Pixels"
                value={values.trackingPixelIds || []}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText="Optional: assign one or more pixels to this event."
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    if (!selected || selected.length === 0) {
                      return "No Pixels";
                    }
                    return trackingPixels
                      .filter((pixel) => selected.includes(pixel.id))
                      .map((pixel) => pixel.name?.trim() || pixel.pixelId)
                      .join(", ");
                  },
                }}
              >
                {trackingPixels.map((pixel) => {
                  const labelBase = pixel.name?.trim() || pixel.pixelId;
                  return (
                    <MenuItem key={pixel.id} value={pixel.id}>
                      {labelBase}
                    </MenuItem>
                  );
                })}
              </TextField>
              <Box sx={{ marginTop: "10px" }}>
                <Button
                  variant="outlined"
                  onClick={() => setAddPixelOpen(true)}
                >
                  Add New Pixel
                </Button>
              </Box>
            </Box>

            <Box sx={{ marginTop: "30px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.showInIndex === true}
                        onChange={(e) => {
                          setFieldValue("showInIndex", e.target.checked);
                        }}
                        name="showInIndex"
                      />
                    }
                    label="Include in public Event listings"
                  />
                  <Tooltip title="When enabled, this event will appear on the public events index page where customers can browse and discover events. When disabled, the event will only be accessible via direct link.">
                    <IconButton size="small" sx={{ padding: 0.5 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <LocationForm
              formikProps={{
                values,
                handleChange,
                setFieldValue,
                setValues,
                handleBlur,
                touched,
                errors,
              }}
              addressHistory={addressHistory}
              nameOverride="Event Location"
              handleTimezoneChanged={(timezone) => {
                setFieldValue("timezone", timezone);
              }}
            />
            <DateTimeForm
              formikProps={{
                values,
                handleChange,
                setFieldValue,
                setFieldTouched,
                touched,
                errors,
              }}
            />
            <TimezoneSelectForm
              formikProps={{
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
              }}
              options={timezoneOptions}
              handleTimezoneChanged={(timezone) => {
                setFieldValue("addressTimezone", timezone);
              }}
            />
            <PerformersForm
              organizationId={organizationId}
              organizationPerformers={organizationPerformers}
              formikProps={{
                values,
                setFieldValue,
              }}
            />
            <DescriptionForm
              formikProps={{
                values,
                handleChange,
              }}
              descriptionState={descriptionState}
              handleStateChange={setDescriptionState}
            />
            <BottomButtons
              show={creatingNew || dirty}
              handleDiscard={() => {
                if (creatingNew) {
                  handleCancelNew();
                } else {
                  setDescriptionState(editorStateFromHtml(descriptionHtml));
                  handleReset();
                }
              }}
            />
            <Dialog
              open={addPixelOpen}
              onClose={() => setAddPixelOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Add New Pixel</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField
                    label="Name"
                    value={newPixelValues.name}
                    onChange={(e) =>
                      setNewPixelValues((current) => ({
                        ...current,
                        name: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    select
                    label="Type"
                    value={newPixelValues.type}
                    onChange={(e) =>
                      setNewPixelValues((current) => ({
                        ...current,
                        type: e.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="meta">Meta</MenuItem>
                  </TextField>
                  <TextField
                    label="Pixel ID"
                    value={newPixelValues.pixelId}
                    onChange={(e) =>
                      setNewPixelValues((current) => ({
                        ...current,
                        pixelId: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAddPixelOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!newPixelValues.pixelId.trim()}
                  onClick={async () => {
                    await dispatch(
                      createTrackingPixel({
                        organizationId,
                        values: {
                          name: newPixelValues.name.trim(),
                          type: newPixelValues.type,
                          pixelId: newPixelValues.pixelId.trim(),
                        },
                      }),
                    );
                    const refreshed = await dispatch(
                      fetchTrackingPixelsByOrganization({ organizationId }),
                    ).unwrap();
                    const refreshedPixels = refreshed?.pixels || [];
                    const createdPixel = refreshedPixels.find(
                      (pixel) => pixel.pixelId === newPixelValues.pixelId.trim(),
                    );
                    if (createdPixel) {
                      const existingSelected = values.trackingPixelIds || [];
                      if (!existingSelected.includes(createdPixel.id)) {
                        setFieldValue("trackingPixelIds", [
                          ...existingSelected,
                          createdPixel.id,
                        ]);
                      }
                    }
                    setNewPixelValues({ name: "", type: "meta", pixelId: "" });
                    setAddPixelOpen(false);
                  }}
                >
                  Add Pixel
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EventBasicInfo;
